import { createAsyncThunk } from '@reduxjs/toolkit';
import { buildTournamentApplicationUserDto } from 'shared/utils/build-tournament-application-user-dto';
import { UpsertTournamentApplicationUserDto } from 'modules/players/domain/interfaces/upsert-player-input.dto';
import { UpsertTournamentPlayerApplicationDto } from 'modules/teams/domain/dtos/upsert-tournament-player-action.dto';
import playersResource from 'modules/players/domain/resources/players-resource';
import { PLAYERS_LIST_TAG } from 'modules/players/store/players-api';
import { baseApi } from 'shared/api/base-api';
import { toApiError } from 'shared/errors';
import { AddTournamentUserToTeam, FetchUserEvents, KickTournamentUserFromTeam } from '../domain/interfaces/actions';

import { EventForMomentCard } from '../domain/interfaces/event';
import { Player } from '../domain/interfaces/player';
import TournamentUsersRepository from '../domain/repositories/player.repository';

const PREFIX = 'players';

/** Состав команды читает карточка команды через RTK Query — сбрасываем её кэш. */
const invalidateTeam = (teamId: number) => baseApi.util.invalidateTags([{ type: 'Team' as const, id: teamId }]);

/**
 * Сбрасывает перечень игроков. Отдельно от тега с `id`: в RTK Query они не
 * пересекаются, поэтому появление и удаление игрока надо объявлять именно тегом
 * списка. Мутации здесь thunk'и — сами теги не инвалидируются.
 */
export const invalidatePlayersList = () => baseApi.util.invalidateTags([PLAYERS_LIST_TAG]);

export const upsertTournamentUser = createAsyncThunk<Player, Omit<UpsertTournamentPlayerApplicationDto, 'teamId'>>(
  `${PREFIX}/upsertTournamentPlayerApplication`,
  async (payload, { dispatch }) => {
    try {
      const { tournamentId, photo } = payload;
      const player: UpsertTournamentApplicationUserDto = buildTournamentApplicationUserDto(payload);

      let result = await TournamentUsersRepository.upsertTournamentApplicationForUser(player, tournamentId);
      const { id: playerId } = result;

      if (photo && photo[0]) {
        if (photo[0] instanceof File) {
          await playersResource.uploadPhoto(photo[0], playerId, tournamentId);
        }
      }

      const user = await TournamentUsersRepository.getUser(playerId);

      // И создание, и правка игрока меняют строку в перечне — перечитываем список.
      dispatch(invalidatePlayersList());

      return user;
    } catch (err) {
      console.error(`${PREFIX}/upsertTournamentPlayerApplication error:`, err);
      throw err;
    }
  },
);

export const deleteTournamentUser = createAsyncThunk<{ status: boolean }, { userId: number }>(
  `${PREFIX}/deleteTournamentUser`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const status = await TournamentUsersRepository.deleteTournamentUser(payload);

      dispatch(invalidatePlayersList());

      return { status: status === 'ok' };
    } catch (error) {
      console.error(`${PREFIX}/deleteTournamentUser error:`, error);

      return rejectWithValue(toApiError(error));
    }
  },
);

export const addTournamentUserToTeam = createAsyncThunk<Player, AddTournamentUserToTeam>(
  `${PREFIX}/addTournamentUserToTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const user = await TournamentUsersRepository.addTournamentUserToTeam(payload);

      dispatch(invalidateTeam(payload.teamId));

      return user;
    } catch (error) {
      console.error(`${PREFIX}/addTournamentUserToTeam error:`, error);

      return rejectWithValue(toApiError(error));
    }
  },
);

export const kickTournamentUserFromTeam = createAsyncThunk<true, KickTournamentUserFromTeam>(
  `${PREFIX}/kickTournamentUserFromTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const result = await TournamentUsersRepository.kickTournamentUserFromTeam(payload);
      dispatch(invalidateTeam(payload.teamId));

      return result;
    } catch (error) {
      console.error(`${PREFIX}/kickTournamentUserFromTeam error:`, error);

      return rejectWithValue(toApiError(error));
    }
  },
);

export const fetchUserEvents = createAsyncThunk<EventForMomentCard[], FetchUserEvents>(
  `${PREFIX}/fetchUserEvents`,
  async (payload, { rejectWithValue }) => {
    try {
      return await TournamentUsersRepository.fetchUsersEvents(payload);
    } catch (error) {
      console.error(`${PREFIX}/fetchUserEvents error:`, error);

      return rejectWithValue(toApiError(error));
    }
  },
);
